import { defineStore } from 'pinia'
import axios from 'axios'
import { CONFIG_SERVICE_ENDPOINT, DRIVER_ENDPOINT, VEHICLE_ENDPOINT } from '@/constants/Endpoints'
import { REFERENCE_TABLE } from '@/constants/ReferenceTables'
import { LOGGER } from '@/util/logger'
import { ref } from 'vue'

export const useConfigStore = defineStore('config', () => {
  let references = ref(new Map())
  let recaptchaLoadingPromise = null
  const driverConfig = ref(null)
  const stopCodes = ref({})
  const tagTypes = ref({})
  let dvsCenterInfo = ref({})
  const envConfiguration = ref(null)

  const getReferenceValue = (tableName, value, showAltDescription = false) => {
    let result
    if (showAltDescription) {
      result = references.value.get(tableName)?.find((r) => r.value == value?.trim())?.altDescription || value?.trim()
    } else {
      result = references.value.get(tableName)?.find((r) => r.value == value?.trim())?.title || value?.trim()
    }
    if (tableName === REFERENCE_TABLE.ERRORS && value?.trim().endsWith('X') && value?.trim() !== 'XXXXX') {
      const contactInfo = references.value.get(REFERENCE_TABLE.ERRORS)?.find((r) => r.value == 'XXXXX')?.title
      if (contactInfo) return `${result}\n\n${contactInfo}`
    }
    return result
  }

  const getPrivilegeStatusConfigDesc = (code) => {
    const privilegeStatusConfig = references.value.get('PRIVILEGE_STATUS') || []
    return privilegeStatusConfig.find((config) => config.code === code)?.descr || code
  }

  const getEnvironmentConfigValues = async (properties) => {
    try {
      const response = await axios.get(CONFIG_SERVICE_ENDPOINT.GET_ENV_CONFIG_VALUES, {
        params: { properties },
      })
      return response.data
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const getReferenceValues = (tableName) => references.value.get(tableName)

  const loadAdvancedVehicleTypes = async () => {
    try {
      const response = await axios.get(CONFIG_SERVICE_ENDPOINT.GET_PUBLIC_MVDLS_REFERENCES, {
        params: { referenceNames: 'advancedVehicleTypes' },
      })
      references.value.set(
        'advancedVehicleTypes',
        Object.entries(response.data.advancedVehicleTypes).map(([value, title]) => ({ value, title })),
      )
    } catch (e) {
      LOGGER.logException(e)
    }
  }

  const loadReferenceData = async (isPublic = false) => {
    try {
      const response = await axios.get(
        isPublic ? CONFIG_SERVICE_ENDPOINT.GET_PUBLIC_REFERENCES : CONFIG_SERVICE_ENDPOINT.GET_REFERENCES,
      )
      for (const referenceName of response.data.data) {
        references.value.set(referenceName.metaName, referenceName.itemList)
      }
    } catch (e) {
      LOGGER.logException(e)
    }
    return references
  }

  const loadDVSCenterInfo = async () => {
    const envConfigData = await getEnvironmentConfigValues(['DVSCENTERPHONE', 'DVSCENTERTTY', 'DVSCENTERHOURS'])
    dvsCenterInfo.value = Object.fromEntries(envConfigData?.data?.map((item) => [item.property, item.value]))
  }

  const loadEnvironmentConfigs = async () => {
    const properties = [
      'DUPLICATE_COUNT_LIMIT',
      'PDPSGrandFatheredExpiryDate',
      'SessionTimeout',
      'MIN_PHOTO_VALID_DAYS',
    ]
    const envConfigs = await getEnvironmentConfigValues(properties)
    envConfiguration.value = Object.fromEntries((envConfigs?.data || []).map((item) => [item.property, item.value]))
  }

  const getDriverConfig = async () => {
    if (driverConfig.value) return driverConfig.value
    try {
      const response = await axios.get(DRIVER_ENDPOINT.CONFIG)
      driverConfig.value = response.data
      return driverConfig.value
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const clearDriverConfig = () => {
    driverConfig.value = null
  }

  const loadVehicleRenewalConfig = async () => {
    stopCodes.value = {}
    tagTypes.value = {}
    try {
      const [stopCodeResponse, tagTypeResponse] = await Promise.all([
        axios.get(VEHICLE_ENDPOINT.STOP_CODES),
        axios.get(VEHICLE_ENDPOINT.TAG_TYPES),
      ])
      stopCodes.value = stopCodeResponse.data.data
      tagTypes.value = tagTypeResponse.data.data
    } catch (e) {
      LOGGER.logException(e)
    }
  }

  const PAGE_TYPE_CONFIG = {
    DL: {
      siteKeyConfig: 'recaptchaSiteKeyDLLogin',
      action: 'LOGIN',
    },
    VS: {
      siteKeyConfig: 'recaptchaSiteKeyVRLogin',
      action: 'LOGIN',
    },
    PreVerify: {
      siteKeyConfig: 'recaptchaSiteKeyPreverifyLogin',
      action: 'LOGIN',
    },
    OD: {
      siteKeyConfig: 'recaptchaSiteKeyODLogin',
      action: 'LOGIN',
    },
    ES: {
      siteKeyConfig: 'recaptchaSiteKeyESLogin',
      action: 'LOGIN',
    },
    Survey: {
      siteKeyConfig: 'recaptchaSiteKeySurvey',
      action: 'SUBMIT',
    },
  }

  const getActionForPageType = (pageType) => {
    const config = PAGE_TYPE_CONFIG[pageType]
    if (!config) {
      const validPageTypes = Object.keys(PAGE_TYPE_CONFIG).join(', ')
      const error = new Error(`Invalid pageType: ${pageType}. Must be one of: ${validPageTypes}`)
      LOGGER.logException(error)
      throw error
    }
    return config.action
  }

  const waitForEnterpriseReady = (timeout = 10000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now()

      const checkEnterprise = () => {
        if (globalThis.grecaptcha?.enterprise) {
          globalThis.grecaptcha.enterprise.ready(resolve)
          return
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error('Timeout waiting for reCAPTCHA enterprise to initialize'))
          return
        }

        setTimeout(checkEnterprise, 100)
      }

      checkEnterprise()
    })
  }

  const createRecaptchaScript = (siteKey, tokenBaseUrl) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = `${tokenBaseUrl}${siteKey}`
      script.async = true
      script.onload = () => {
        LOGGER.logEvent('reCAPTCHA script loaded')
        resolve()
      }
      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA script'))
      }
      document.head.appendChild(script)
    })
  }

  const resolveRecaptchaConfig = async (pageType) => {
    const config = PAGE_TYPE_CONFIG[pageType]
    const response = await getEnvironmentConfigValues([config.siteKeyConfig, 'recaptchaTokenUrl', 'reCaptchaEnable'])
    const find = (property) => response.data.find((item) => item.property === property)?.value
    return {
      siteKey:
        import.meta.env.VITE_NODE_ENV === 'development'
          ? import.meta.env.VITE_APP_CAPTCHA_SITEKEY
          : find(config.siteKeyConfig),
      tokenBaseUrl: find('recaptchaTokenUrl'),
      enabled: find('reCaptchaEnable') === 'true',
    }
  }

  const loadRecaptcha = async (pageType) => {
    const { siteKey, tokenBaseUrl, enabled } = await resolveRecaptchaConfig(pageType)

    if (!enabled) return { siteKey, enabled }

    if (globalThis.grecaptcha?.enterprise) {
      LOGGER.logEvent('reCAPTCHA is already loaded')
      return { siteKey, enabled }
    }

    if (recaptchaLoadingPromise) {
      LOGGER.logEvent('reCAPTCHA is already loading, waiting...')
      await recaptchaLoadingPromise
      return { siteKey, enabled }
    }

    LOGGER.logEvent('Loading reCAPTCHA script...')

    recaptchaLoadingPromise = createRecaptchaScript(siteKey, tokenBaseUrl)
      .then(() => waitForEnterpriseReady())
      .then(() => {
        recaptchaLoadingPromise = null
      })
      .catch((error) => {
        recaptchaLoadingPromise = null
        LOGGER.logException(error)
        throw error
      })

    await recaptchaLoadingPromise
    return { siteKey, enabled }
  }

  const getCaptchaToken = async (siteKey, action) => {
    try {
      LOGGER.logEvent('Generating reCAPTCHA token...')
      const token = await globalThis.grecaptcha.enterprise.execute(siteKey, { action })
      LOGGER.logEvent('reCAPTCHA token generated')
      return token
    } catch (error) {
      LOGGER.logException(error)
      throw error
    }
  }

  const buildRecaptchaPayload = async (pageType) => {
    const { siteKey, enabled } = await loadRecaptcha(pageType)

    if (!enabled) return null

    const action = getActionForPageType(pageType)

    const payload = {
      expectedAction: action,
      siteKey,
      pageType,
    }
    return payload
  }

  const normalizeAddress = async (addressData) => {
    try {
      const payload = {
        addressVerification: {
          addressLine1: addressData.addressLine1 || '',
          addressLine2: addressData.addressLine2 || '',
          poBox: addressData.poBox || '',
          city: addressData.city || '',
          state: addressData.state || '',
          zip5: addressData.zip5 || '',
          zip4: addressData.zip4 || '',
          countyCode: addressData.countyCode || '',
          idNumber: addressData.idNumber,
        },
      }

      const response = await axios.post(CONFIG_SERVICE_ENDPOINT.NORMALIZE_ADDRESS, payload)
      return response.data
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  return {
    references,
    loadReferenceData,
    loadAdvancedVehicleTypes,
    getReferenceValue,
    getReferenceValues,
    getPrivilegeStatusConfigDesc,
    getEnvironmentConfigValues,
    getDriverConfig,
    clearDriverConfig,
    stopCodes,
    tagTypes,
    loadVehicleRenewalConfig,
    buildRecaptchaPayload,
    normalizeAddress,
    dvsCenterInfo,
    loadDVSCenterInfo,
    getCaptchaToken,
    loadEnvironmentConfigs,
    envConfiguration,
  }
})
